import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FONT_FAMILY, MONO_FONT_FAMILY } from "./designTokens";

/** The designs' `.custom-select` (NeuraDesigns/assets/neura.css), rebuilt rather than
 *  restyled onto MUI's Select.
 *
 *  Eight Field Admin screens specify this control. Restyling MUI gets the closed trigger
 *  right and leaves the open menu on defaults — grey hover, MUI's own selected tint, its
 *  padding — because the popover renders its own markup in a portal that MenuProps cannot
 *  fully reach. The stylesheet also describes more than a list: a search box once the list
 *  is long, an empty state, and options carrying a sub-line, a monospace code and a flag.
 *
 *  Tokens resolved from :root in that stylesheet, so the values below are the source's:
 *    surface #161B22 · border rgba(255,255,255,0.06) · border-strong rgba(255,255,255,0.12)
 *    text-faint rgba(255,255,255,0.35) · accent #FDB940 · accent-tint rgba(253,185,64,0.12)
 *    radius-md 8px · radius-xl 12px · shadow-pop 0 10px 30px rgba(0,0,0,0.45)
 *
 *  This is a copy of the component IMS carries, not an import: the two live in separate
 *  repositories and databases. It differs in one way — `.custom-select__opt.is-active` is
 *  implemented here, so the list answers the arrow keys. A dropdown that only responds to
 *  a mouse is a poor control on a screen used one-handed in a van.
 */

const ACCENT = "#FDB940";
const ACCENT_TINT = "rgba(253, 185, 64, 0.12)";
const TEXT_FAINT = "rgba(255, 255, 255, 0.35)";
const SURFACE = "#161B22";
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

export default function DesignSelect({
  value,
  onChange,
  options,                 // [{ value, label, sub, code, flag, disabled }]
  placeholder = "Choose…",
  disabled = false,
  searchable,              // defaults on once the list is long enough to need it
  emptyText = "Nothing to choose from.",
  id,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  const showSearch = searchable ?? options.length > 7;

  const shown = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) =>
      `${o.label} ${o.sub ?? ""} ${o.code ?? ""}`.toLowerCase().includes(q));
  }, [options, query]);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) { setQuery(""); setActiveIndex(-1); return undefined; }
    const onDocClick = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Keeps the keyboard-active option in view as it moves past the panel's 220px window.
  useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const pick = (option) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
  };

  const step = (delta) => {
    if (!shown.length) return;
    setActiveIndex((prev) => {
      let next = prev;
      // Skips disabled options rather than parking on one that cannot be chosen.
      for (let i = 0; i < shown.length; i += 1) {
        next = (next + delta + shown.length) % shown.length;
        if (!shown[next].disabled) return next;
      }
      return prev;
    });
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault(); setOpen(true); return;
    }
    if (!open) return;
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "ArrowDown") { e.preventDefault(); step(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); step(-1); }
    else if (e.key === "Enter" && activeIndex >= 0) { e.preventDefault(); pick(shown[activeIndex]); }
  };

  return (
    <Box ref={rootRef} sx={{ position: "relative" }}>
      {/* .custom-select__trigger */}
      <Box
        component="button"
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px",
          width: "100%", padding: "10px 14px", boxSizing: "border-box",
          background: disabled ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "8px",
          color: disabled ? "rgba(255,255,255,0.75)" : "#fff",
          fontFamily: FONT_FAMILY, fontSize: "0.88rem", fontWeight: 400,
          cursor: disabled ? "default" : "pointer",
          transition: `all 0.16s ${EASE}`,
          "&:hover": { borderColor: disabled ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.16)" },
          ...(open && { borderColor: "rgba(253,185,64,0.45)", boxShadow: `0 0 0 3px ${ACCENT_TINT}` }),
        }}
      >
        {/* .custom-select__value */}
        <Box component="span" sx={{
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left",
          color: selected ? "inherit" : TEXT_FAINT,
        }}>
          {selected?.label ?? placeholder}
        </Box>
        <KeyboardArrowDownIcon sx={{
          width: "14px", height: "14px", flexShrink: 0, opacity: disabled ? 0.3 : 0.6,
          transition: `transform 0.16s ${EASE}`,
          transform: open ? "rotate(180deg)" : "none",
        }} />
      </Box>

      {/* .custom-select__panel */}
      {open ? (
        <Box sx={{
          position: "absolute", zIndex: 220, top: "calc(100% + 6px)", left: 0, right: 0,
          background: SURFACE, border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "12px", boxShadow: "0 10px 30px rgba(0,0,0,0.45)", padding: "8px",
        }}>
          {showSearch ? (
            // .custom-select__search
            <Box sx={{
              display: "flex", alignItems: "center", gap: "8px", padding: "7px 10px", mb: "6px",
              background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "8px",
            }}>
              <SearchIcon sx={{ width: "13px", height: "13px", color: TEXT_FAINT, flexShrink: 0 }} />
              <Box
                component="input"
                autoFocus
                placeholder="Search…"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setActiveIndex(-1); }}
                onKeyDown={onKeyDown}
                sx={{
                  flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none",
                  color: "#fff", fontFamily: FONT_FAMILY, fontSize: "0.82rem",
                  "&::placeholder": { color: TEXT_FAINT },
                }}
              />
            </Box>
          ) : null}

          {/* .custom-select__list */}
          <Box component="ul" ref={listRef} role="listbox" sx={{ listStyle: "none", m: 0, p: 0, maxHeight: "220px", overflowY: "auto" }}>
            {shown.length === 0 ? (
              // .custom-select__empty
              <Typography sx={{ padding: "12px 10px", fontSize: "0.78rem", color: TEXT_FAINT, textAlign: "center" }}>
                {query.trim() ? "No matches." : emptyText}
              </Typography>
            ) : shown.map((option, index) => {
              const isSelected = option.value === value;
              const isActive = index === activeIndex;
              return (
                <Box
                  component="li"
                  key={String(option.value)}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled || undefined}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pick(option)}
                  sx={{
                    display: "flex", alignItems: "center", gap: "8px",
                    padding: "8px 10px", borderRadius: "8px",
                    cursor: option.disabled ? "not-allowed" : "pointer",
                    fontSize: "0.85rem", color: option.disabled ? TEXT_FAINT : "#fff",
                    opacity: option.disabled ? 0.55 : 1,
                    transition: `background 0.12s ${EASE}`,
                    "&:hover": { background: option.disabled ? "none" : ACCENT_TINT },
                    // .is-selected then .is-active — the ring marks where the keyboard is,
                    // which is a different question from what is currently chosen.
                    ...(isSelected && {
                      background: ACCENT_TINT, color: ACCENT, fontWeight: 600,
                      boxShadow: "inset 0 0 0 1px rgba(253,185,64,0.3)",
                    }),
                    ...(isActive && !option.disabled && { boxShadow: `inset 0 0 0 1px ${ACCENT}` }),
                  }}
                >
                  {/* .custom-select__opt-text */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                    <Box component="span" sx={{ fontWeight: isSelected || option.sub ? 600 : 400 }}>
                      {option.label}
                      {option.code ? (
                        <Box component="span" sx={{ fontFamily: MONO_FONT_FAMILY, color: ACCENT, ml: "6px" }}>
                          {option.code}
                        </Box>
                      ) : null}
                    </Box>
                    {option.sub ? (
                      <Box component="span" sx={{ fontSize: "0.74rem", color: TEXT_FAINT, fontWeight: 400 }}>
                        {option.sub}
                      </Box>
                    ) : null}
                  </Box>
                  {option.flag ? (
                    // .custom-select__opt-flag
                    <Box component="span" sx={{
                      ml: "auto", flexShrink: 0, fontSize: "0.62rem", fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.04em", padding: "2px 6px",
                      borderRadius: "4px", background: "rgba(244,114,182,0.14)", color: "#F472B6",
                      border: "1px solid rgba(244,114,182,0.32)", whiteSpace: "nowrap",
                    }}>
                      {option.flag}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
